import React, { useEffect, useState, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import { useNotify, Notification } from "react-admin";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import Background from "./assets/bg-desktop.png";
import teAyudoLogo from "./assets/te-ayudo-prov.png";
import { useRedirect } from "react-admin";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TyC from "./TyC";
import { StyledEngineProvider } from "@mui/material/styles";
import { Box, MenuItem, Select } from "@mui/material";
import authProvider from "./authProvider";
import dataProvider from "./components/dataProvider";

export const SERVICE_METHODS = Object.freeze({
  DELIVERY: "A domicilio",
  IN_BRANCH: "En sucursal",
  BOTH: "Ambos",
});

const PREFIX = "Register";

const classes = {
  switchBase: `${PREFIX}-switchBase`,
  checked: `${PREFIX}-checked`,
  track: `${PREFIX}-track`,
};

const StyledStyledEngineProvider = styled(StyledEngineProvider)({
  [`& .${classes.switchBase}`]: {
    color: "#FF770D",
    "&$checked": {
      color: "#FF770D",
    },
    "&$checked + $track": {
      backgroundColor: "#FF770D",
    },
  },
  [`& .${classes.checked}`]: {},
  [`& .${classes.track}`]: {},
});

const styles = {
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    minWidth: 300,
    backgroundColor: "transparent",
  },
  avatar: {
    margin: "1em",
    textAlign: "center ",
  },
  form: {
    padding: "0 1em 1em 1em",
  },
  input: {
    display: "flex",
    marginBottom: 15,
    boxSizing: "content-box",
  },
  hint: {
    textAlign: "center",
    marginTop: "1em",
    color: "#ccc",
  },
};

const countries = [
  { code: "+54", name: "Argentina" },
  { code: "+591", name: "Bolivia" },
  { code: "+56", name: "Chile" },
  { code: "+57", name: "Colombia" },
  { code: "+506", name: "Costa Rica" },
  { code: "+599", name: "Curazao" },
  { code: "+593", name: "Ecuador" },
  { code: "+503", name: "El Salvador" },
  { code: "+34", name: "España" },
  { code: "+1", name: "Estados Unidos" },
  { code: "+502", name: "Guatemala" },
  { code: "+504", name: "Honduras" },
  { code: "+39", name: "Italia" },
  { code: "+52", name: "México" },
  { code: "+505", name: "Nicaragua" },
  { code: "+507", name: "Panamá" },
  { code: "+595", name: "Paraguay" },
  { code: "+51", name: "Perú" },
  { code: "+1", name: "Puerto Rico" },
  { code: "+1", name: "República Dominicana" },
  { code: "+598", name: "Uruguay" },
  { code: "+58", name: "Venezuela" },
];

function TyCModal({ handleClose, open }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <div>
      <Dialog fullScreen={fullScreen} open={open} onClose={handleClose} aria-labelledby="responsive-dialog-title">
        <DialogTitle id="responsive-dialog-title">{"Términos y Condiciones de Te Ayudo"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <TyC />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const Register = () => {
  const muiTheme = createTheme({
    palette: {
      primary: {
        main: "#FF770D",
      },
      secondary: {
        main: "#000",
      },
    },
  });

  const [businessType, setBusinessType] = useState("");
  const [professionals, setProfessionals] = useState("");
  const [servicePreference, setServicePreference] = useState("");
  const [country, setCountry] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [business_name, setBusiness_name] = useState("");
  const [categories, setCategories] = useState([]);
  const redirect = useRedirect();
  const notify = useNotify();
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const loadCategories = useCallback(async () => {
    try {
      const result = await dataProvider("GET_LIST", "categories", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "name", order: "ASC" },
        filter: { state: true }, // Solo categorías activas
      });
      setCategories(result.data || []);
    } catch (error) {
      notify("Error al cargar las categorías", "warning");
    }
  }, [notify]);

  useEffect(() => {
    setOpen(true);
    // Cargar categorías
    loadCategories();
  }, [loadCategories]);

  const handleBusinessTypeChange = (e) => {
    const value = e.target.value;

    // Si selecciona "otro", redirigir a WhatsApp
    if (value === "otro") {
      window.open(
        "https://wa.me/59177142692?text=Hola, necesito ayuda para agregar mi tipo de negocio a Te Ayudo",
        "_blank"
      );
      // No cambiar el valor del select para que siga mostrando el placeholder
      return;
    }

    setBusinessType(value);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (businessType && professionals && servicePreference && business_name && email && country && phone && password) {
      try {
        // Buscar la categoría seleccionada para obtener nombre e id
        const selectedCategory = categories.find((cat) => cat.id === businessType);

        // Usar authProvider.register para auto-login después del registro
        await authProvider.register({
          businessType: {
            idCategoria: selectedCategory?.id,
            nombreCategoria: selectedCategory?.name,
          },
          numberOfProfessionals: professionals,
          type: SERVICE_METHODS[servicePreference], // Convertir clave a valor
          business_name, // string
          email,
          country, // { code, countryName }
          phone, // phone without code
          password, // string
          role: "proveedor-empresa",
          provider_verified: true,
          state: true,
          first_name: business_name,
          providerEnabledForApp: true,
          providerEnabledForWeb: true,
          reminderConfirmed: true,
          whatsappLinked: true,
        });

        // Si llegamos aquí, el registro fue exitoso y el usuario ya está logueado
        notify("Cuenta registrada con éxito. ¡Bienvenido!");
        redirect("/"); // Redirigir al dashboard principal
      } catch (error) {
        // Maneja específicamente el error de email ya registrado
        let errorMessage = "";

        // Si el error es un string directo del backend
        if (typeof error === "string") {
          errorMessage = error;
        }
        // Si el error tiene una propiedad message
        else if (error?.message) {
          errorMessage = error.message;
        }
        // Si el error tiene una propiedad error (como viene del backend)
        else if (error?.error) {
          errorMessage = error.error;
        }
        // Fallback
        else {
          errorMessage = error?.toString() || "Error desconocido";
        }

        // Verifica si es el error específico de email ya registrado
        if (errorMessage.includes("Email ya está registrado") || errorMessage.includes("ya está registrado")) {
          notify("Este email ya está registrado. Por favor, usa otro email o inicia sesión.", "warning");
        } else {
          notify(`Error al crear cuenta: ${errorMessage}`, "warning");
        }
      }
    } else {
      notify("Debes llenar todos los campos.", "warning");
    }
    //}
  };

  return (
    <StyledStyledEngineProvider injectFirst>
      <ThemeProvider theme={muiTheme}>
        <div
          style={{
            ...styles.main,
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#252526",
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${Background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            p: 2,
          }}
        >
          <Card style={styles.card}>
            <div style={{ ...styles.avatar, display: "flex", justifyContent: "center" }}>
              <Avatar
                backgroundColor="transparent"
                src={teAyudoLogo}
                variant="square"
                style={{ height: 101, width: 254, borderRadius: 0 }}
              />
            </div>
            <form onSubmit={submit}>
              <div style={styles.form}>
                <div style={{ color: "black", marginBottom: 15 }}>
                  <Select
                    fullWidth
                    value={businessType}
                    onChange={handleBusinessTypeChange}
                    variant="outlined"
                    displayEmpty
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                  >
                    <MenuItem value="">¿Qué tipo de negocio tienes?</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="otro">Otro</MenuItem>
                  </Select>
                </div>
                <div style={{ color: "black", marginBottom: 15 }}>
                  <Select
                    fullWidth
                    value={professionals}
                    onChange={(e) => setProfessionals(e.target.value)}
                    variant="outlined"
                    displayEmpty
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                  >
                    <MenuItem value="">¿Cuántos profesionales hay en tu negocio?</MenuItem>
                    <MenuItem value="independiente">Soy profesional independiente</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                    <MenuItem value="3-5">3-5</MenuItem>
                    <MenuItem value="6-15">6-15</MenuItem>
                    <MenuItem value="16+">16+</MenuItem>
                  </Select>
                </div>

                <div style={{ color: "black", marginBottom: 15 }}>
                  <Select
                    fullWidth
                    value={servicePreference}
                    onChange={(e) => setServicePreference(e.target.value)}
                    variant="outlined"
                    displayEmpty
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                  >
                    <MenuItem value="">¿Cómo ofreces tus servicios?</MenuItem>
                    {Object.entries(SERVICE_METHODS).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div style={styles.input}>
                  <TextField
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                    fullWidth
                    variant="filled"
                    label="Nombre de Empresa"
                    name="business-name"
                    type="text"
                    value={business_name}
                    onChange={(e) => setBusiness_name(e.target.value)}
                  />
                </div>

                <div style={{ ...styles.input, marginBottom: 15 }}>
                  <TextField
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                    fullWidth
                    variant="filled"
                    label="Email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <div style={styles.input}>
                    <Select
                      value={country.name || ""}
                      onChange={(e) => {
                        const selected = countries.find((c) => c.name === e.target.value);
                        if (selected) setCountry(selected);
                      }}
                      variant="outlined"
                      displayEmpty
                      style={{ flex: 1, backgroundColor: "white", borderRadius: 5 }}
                    >
                      <MenuItem value="">País</MenuItem>
                      {countries.map((c, index) => (
                        <MenuItem key={index} value={c.name}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div style={styles.input}>
                    <TextField
                      variant="outlined"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Número sin código"
                      style={{ flex: 1, background: "white", borderRadius: 5 }}
                      InputProps={{
                        startAdornment: (
                          <span style={{ marginRight: 8, fontWeight: "bold" }}>{country.code || "+00"}</span>
                        ),
                      }}
                    />
                  </div>
                </div>

                <div style={{ ...styles.input, marginBottom: 0 }}>
                  <TextField
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                    fullWidth
                    variant="filled"
                    label="Contraseña"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {/* <div style={styles.input}>
                  <TextField
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                    fullWidth
                    variant="filled"
                    label="Confirmar Contraseña"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div> */}
                {/* <div style={styles.input}>
                  <TextField
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                    fullWidth
                    variant="filled"
                    label="Razón Social"
                    name="business_name"
                    type="text"
                    value={business_name}
                    onChange={(e) => setBusiness_name(e.target.value)}
                  />
                </div>
                <div style={styles.input}>
                  <TextField
                    style={{ backgroundColor: "white", borderRadius: 5 }}
                    fullWidth
                    variant="filled"
                    label="NIT"
                    name="nit"
                    type="text"
                    value={nit}
                    onChange={(e) => setNit(e.target.value)}
                  />
                </div> */}
                {/* <div style={styles.input}>
                    <TextField
                      style={{ backgroundColor: "white", borderRadius: 5 }}
                      fullWidth
                      variant="filled"
                      label="Número de Teléfono"
                      name="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div> */}
              </div>
              <TyCModal handleClose={handleClose} open={open}></TyCModal>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <Button
                  variant="contained"
                  type="submit"
                  style={{
                    width: 375,
                    color: "#fff",
                    height: 48,
                    borderRadius: 5,
                  }}
                >
                  Crear cuenta
                </Button>
                <div
                  style={{
                    color: "white",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginBottom: 10,
                    marginTop: 10,
                  }}
                >
                  Al crear tu cuenta aceptas nuestros <br />
                  <button
                    type="button"
                    onClick={handleClickOpen}
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      textDecoration: "underline",
                      cursor: "pointer",
                      padding: 0,
                      font: "inherit",
                    }}
                  >
                    {" "}
                    Términos y Condiciones
                  </button>
                  {/* <OrangeSwitch
                    checked={tyc}
                    onChange={() => setTyc(!tyc)}
                    name="checkedB"
                    classes={{
                      switchBase: classes.switchBase,
                      checked: classes.checked,
                      track: classes.track
                    }} /> */}
                </div>
              </Box>
            </form>
          </Card>
          <Notification />
        </div>
      </ThemeProvider>
    </StyledStyledEngineProvider>
  );
};

export default Register;
